import { Box, Button, Card, Group, Stack, Text } from '@mantine/core'
import { IconMessages, IconPlus } from '@tabler/icons-react'
import { forwardRef, useImperativeHandle } from 'react'

import { InputLabel } from '@/components/InputLabel'
import { theme } from '@/configs/theme'
import { useFAQManagement } from '@/hooks/useFAQManagement'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { FAQEntry } from '@/screens/ContentModuleFormScreen/FAQEntry'
import { PendingFAQEntry } from '@/screens/ContentModuleFormScreen/PendingFAQEntry'

interface FAQSectionProps extends CMFormSection {
  type: 'cm' | 'step'
}

export interface FAQSectionRef {
  saveUnsavedFAQs: () => Promise<void>
}

export const FAQSection = forwardRef<FAQSectionRef, FAQSectionProps>(function FAQSection(props, ref) {
  const {
    pending: pendingFAQs,
    approved: approvedFAQs,
    totalCount: totalFAQCount,
    isGenerating: isGeneratingFAQs,
    showValidationErrors,
    handleAddFAQ,
    handleGenerateFAQs,
    handleUpdateFAQ,
    handleDeleteFAQ,
    handleApproveFAQ,
    handleRejectFAQ,
    handleApproveAllFAQs,
    saveUnsavedFAQs,
  } = useFAQManagement(props)

  // Expose the save function to parent components
  useImperativeHandle(
    ref,
    () => ({
      saveUnsavedFAQs,
    }),
    [saveUnsavedFAQs],
  )

  const label = props.type === 'cm' ? 'Module-Level FAQs' : 'File FAQs'
  const description = props.type === 'cm' ? 'Add any FAQs for the module not covered under file FAQs.' : ''

  return (
    <Box>
      <Group justify="space-between" align="center" mb="lg">
        <InputLabel label={label} description={description} withSpacer={false} />

        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={handleAddFAQ} size="sm">
          Add FAQ
        </Button>
      </Group>

      <Stack gap="lg" mt="md">
        {/* Approved/Manual FAQs */}
        <Box>
          {approvedFAQs.length > 0 ? (
            <Stack gap="md">
              {approvedFAQs.map(faq => (
                <FAQEntry
                  key={faq.id}
                  faq={faq}
                  onUpdate={handleUpdateFAQ}
                  onDelete={handleDeleteFAQ}
                  showValidationErrors={showValidationErrors}
                />
              ))}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" mb="md">
              Click &quot;Add FAQ&quot; to create one manually or approve FAQs generated by AI.
            </Text>
          )}
        </Box>

        {/* Pending AI-Generated FAQs */}
        {pendingFAQs.length > 0 && (
          <Box>
            <Group justify="space-between" align="center" mb="sm">
              <Text size="sm" fw={500} c="indigo">
                AI Generated FAQs ({pendingFAQs.length})
              </Text>

              <Button size="xs" variant="gradient" onClick={handleApproveAllFAQs}>
                Approve All
              </Button>
            </Group>

            <Stack gap="md">
              {pendingFAQs.map(faq => (
                <PendingFAQEntry key={faq.id} faq={faq} onApprove={handleApproveFAQ} onReject={handleRejectFAQ} />
              ))}
            </Stack>
          </Box>
        )}

        {/* Show loading state while generating FAQs */}
        {isGeneratingFAQs && (
          <Card
            withBorder
            p="xl"
            style={{
              textAlign: 'center',
              backgroundImage: `linear-gradient(120deg, ${theme.alpha(theme.colors.primaryColors[6], 0.15)} 0%, ${theme.alpha(theme.colors.cyan[4], 0.1)} 100%)`,
              borderColor: theme.colors.primaryColors[6],
              borderWidth: 1,
            }}>
            <IconMessages size={48} color={theme.colors.primaryColors[7]} style={{ margin: '0 auto 16px' }} />
            <Text size="lg" fw={500} mb="xs" style={{ color: theme.colors.primaryColors[8] }}>
              Generating FAQs...
            </Text>
            <Text size="sm" mb="xl" style={{ color: theme.colors.primaryColors[7] }}>
              Sidekick is creating relevant FAQs based on your content. This will just take a moment.
            </Text>
          </Card>
        )}

        {/* Empty State - Only show when no FAQs at all and not generating */}
        {totalFAQCount === 0 && !isGeneratingFAQs && (
          <Card withBorder p="xl" style={{ textAlign: 'center' }}>
            <IconMessages size={48} color="var(--mantine-color-gray-5)" style={{ margin: '0 auto 16px' }} />
            <Text size="lg" fw={500} mb="xs">
              No FAQs Added
            </Text>
            <Text size="sm" c="dimmed" mb="xl">
              {props.type === 'cm'
                ? 'Add any FAQs not covered in the primary content files. You can have Sidekick auto generate a list that you can edit, you can upload a document of FAQs, or you can manually add FAQs.'
                : 'You can have Sidekick auto generate an FAQ list that you can edit, or you can enter FAQs manually.'}
            </Text>
          </Card>
        )}
      </Stack>

      {/* Generate Button */}
      {!isGeneratingFAQs && (
        <Group justify="center" mt="xl">
          <Button
            variant={totalFAQCount === 0 ? 'gradient' : 'light'}
            leftSection={<IconMessages size={16} />}
            onClick={handleGenerateFAQs}
            size="sm">
            {totalFAQCount === 0 ? 'Ask Sidekick to Generate FAQs' : 'Generate More FAQs'}
          </Button>
        </Group>
      )}
    </Box>
  )
})
